import { useEffect, useRef, useState } from "react";
import { Field, Form } from "react-final-form";
import { Conversation, Message } from "@twilio/conversations";
import { FormApi } from "final-form";
import UiButton from "../../../components/button/Button";
import UiMessage from "../../../components/message/Message.component";
import { MessageSeverity } from "../../../models/components/field-message.model";
import {
	CareMessageClientStatus,
	ConversationMessageForm,
} from "../../../models/pages/care-messages.model";
import { Validations } from "../../../services/form/validations.service";
import { useConversationMembers } from "../../../hooks/useConversationMembers";
import TwilioConversationService from "../../../services/twilio/twilio-conversations.service";
import ConversationMessage from "./ConversationMessage";
import { getLocalStorageUser } from "../../../services/session/auth.service";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { useTranslation } from "react-i18next";
import UiFieldErrors from "../../../components/field-errors/FieldErrors.component";
import CareMessagesClientState from "./CareMessagesClientState";
import { useMatomo } from "@jonkoops/matomo-tracker-react";
import { MatomoAction, MatomoCategory } from "../../../models/matomo.model";

const CareMessages = ({ clientState }: { clientState: CareMessageClientStatus }) => {
	const { t } = useTranslation("common");
	const matomo = useMatomo();

	const [loadingConversation, setLoadingConversation] = useState<boolean>(true);
	const participant = getLocalStorageUser();

	const conversationMembers = useConversationMembers({
		participantId: participant.id,
		participantFirstName: participant.firstName,
		participantLastName: participant.lastName,
	});

	const [messages, setMessages] = useState<Message[]>([]);
	const [prevMessages, setPrevMessages] = useState<Message[]>([]);

	// Messages container (scroll to top when laoding previous messages)
	const messagesContainerRef = useRef<HTMLDivElement>(null);

	// The bottom of the messages container (scroll to bottom when sending a new message)
	const messagesEndRef = useRef<HTMLDivElement>(null);

	// Spinner: loading previous messages
	const [loadingMessages, setLoadingMessages] = useState<boolean>(false);

	// Form: send new message
	const [formData, setFormData] = useState<ConversationMessageForm>({
		message: null,
		error: null,
	});

	// When loaded for the 1st time
	useEffect(() => {
		setLoadingConversation(true);

		getConversation();
		// Listen to the conversation changes
		const conversationSubscription = TwilioConversationService.conversationSubject.subscribe(
			(_event: Conversation | null) => {
				if (TwilioConversationService.conversation) {
					getMessages().finally(() => scrollToBottom());
				}
			}
		);

		// Listen to the messages changes (messageAdded - sent/received)
		const messageAddedSubscription = TwilioConversationService.messageAddedSubject.subscribe(
			(event: Message | undefined) => {
				if (event) {
					getMessages().finally(() => scrollToBottom());
				}
			}
		);

		// Remove listeners
		return () => {
			if (conversationSubscription) conversationSubscription.unsubscribe();
			if (messageAddedSubscription) messageAddedSubscription.unsubscribe();
		};
	}, []);

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	useEffect(() => {
		scrollToTop();
	}, [prevMessages]);

	/**
	 * Conversation: get the conversation SID
	 * @description Check if there is already a conversation with this participant. If there is return the SID.
	 */
	const getConversation = async (): Promise<void> => {
		if (!TwilioConversationService.conversation) {
			TwilioConversationService.getConversation(participant.id).finally(() =>
				setLoadingConversation(false)
			);
		} else {
			setLoadingConversation(false);
		}
	};

	// Get the conversation messages
	const getMessages = async (): Promise<void> => {
		if (TwilioConversationService.conversation) {
			setLoadingMessages(true);
			TwilioConversationService.getMessages()
				.then((response) => {
					setMessages(response);
				})
				.finally(() => setLoadingMessages(false));
		}
	};

	// Get previous messages (when scrolling to top)
	const getPreviousMessages = async (): Promise<void> => {
		// Scrolled to bottom
		// if (scrollTop + clientHeight === scrollHeight)

		// Scrolled to top
		if (
			messagesContainerRef.current?.scrollTop === 0 &&
			TwilioConversationService.messagesPaginator?.hasPrevPage
		) {
			setLoadingMessages(true);
			TwilioConversationService.getPreviousMessages()
				.then((response) => {
					setPrevMessages(response);
				})
				.finally(() => setLoadingMessages(false));
		}
	};

	const scrollToTop = (): void =>
		messagesContainerRef.current?.scrollTo({ top: 10, behavior: "smooth" });

	const scrollToBottom = (): void =>
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

	// Submit: send a new message
	const handleSubmit = async (
		_data: ConversationMessageForm,
		form: FormApi<ConversationMessageForm, ConversationMessageForm>
	) => {
		setFormData({ ...formData, error: null });

		await TwilioConversationService.sendMessage(`${formData.message}`, participant.id)
			.then((_response) => {
				matomo.trackEvent({
					category: MatomoCategory.MESSAGES,
					action: MatomoAction.MESSAGE_SENT,
				});

				// When sending a new message, scroll to bottom
				scrollToBottom();

				// Reset message form
				setFormData({ ...formData, message: null, error: null });
				form.resetFieldState("message");
			})
			.catch((_error) => {
				// Show error message
				setFormData({
					...formData,
					error: "MESSAGES.ERROR_SEND",
				});
			});
	};

	return (
		<>
			{/* Loading spinner */}
			{loadingConversation ? (
				<UiMessage
					severity={MessageSeverity.LOADING}
					label="MESSAGES.LOADING"
				/>
			) : (
				<div>
					{/* Message: history */}
					<div
						className="messages-history"
						onScroll={getPreviousMessages}
						ref={messagesContainerRef}
					>
						{/* State: client/conversation/messages */}
						{(clientState === CareMessageClientStatus.LOADING ||
							clientState === CareMessageClientStatus.ERROR ||
							loadingConversation ||
							loadingMessages) && (
								<CareMessagesClientState
									clientState={clientState}
									loadingConversation={loadingConversation}
									loadingMessages={loadingMessages}
								/>
							)}

						{/* Messages: list */}
						{prevMessages.map((message) => (
							<ConversationMessage
								key={message.sid}
								message={message}
								members={conversationMembers.data ?? {}}
							/>
						))}
						{messages.map((message) => (
							<ConversationMessage
								key={message.sid}
								message={message}
								members={conversationMembers.data ?? {}}
							/>
						))}

						{/* Messages: dummy div to scroll to when a new messages is sent/received */}
						<div ref={messagesEndRef} />
					</div>

					{/* Message: write */}
					<Form
						onSubmit={handleSubmit}
						initialValues={formData}
						render={({ handleSubmit, form, submitting }) => (
							<form onSubmit={handleSubmit}>
								<Field
									name="message"
									validate={Validations.composeValidators([Validations.required])}
									render={({ input, meta }) => (
										<>
											<div
												className={classNames("messages-write", {
													"p-invalid": !!(meta.touched && meta.error),
												})}
											>
												<InputText
													placeholder={t("MESSAGES.PLACEHOLDER")}
													name={input.name}
													value={input.value}
													onChange={(e) =>
														setFormData({
															...formData,
															message: e.target.value,
														})
													}
													onBlur={(_e) => {
														if (input) input.onBlur();
													}}
													disabled={
														submitting ||
														clientState ===
														CareMessageClientStatus.ERROR
													}
												/>
												<UiButton
													type="submit"
													label={
														submitting
															? "MESSAGES.BUTTON_SENDING"
															: "MESSAGES.BUTTON_SEND"
													}
													disabled={
														submitting ||
														clientState ===
														CareMessageClientStatus.ERROR
													}
												/>
											</div>
											{/* Validation messages */}
											<UiFieldErrors {...meta} />
										</>
									)}
								/>
							</form>
						)}
					/>

					{formData.error && (
						<UiMessage
							severity={MessageSeverity.DANGER}
							label={formData.error}
						/>
					)}
				</div>
			)}
		</>
	);
};

export default CareMessages;
